import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { DialogStatus } from 'src/app/core/services/dialog.service';
import { FormBtnsModule } from '../../form-btns/form-btns.module';
import { MessageDialogComponent } from '../../message-dialog/message-dialog.component';
import { ModalTitleComponent } from '../../modal-title/modal-title.component';
import { TableColumnStateService } from '../table-column-state.service';

@Component({
  selector: 'table-column-settings',
  templateUrl: './table-column-settings.component.html',
  styleUrls: ['./table-column-settings.component.scss'],
  imports: [
    CdkDropList,
    CdkDrag,
    ModalTitleComponent,
    CommonModule,
    TranslateModule,
    InlineSVGModule,
    FormBtnsModule,
  ],
})
export class TableColumnSettingsComponent {
  //#region properties
  pageName: string;
  columnsList: ColumnType[] = [];
  loading = false;
  selectAll = false;
  //#endregion

  constructor(
    private dialogRef: MatDialogRef<TableColumnSettingsComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    this.initializeColumnsList();
    this.pageName = this.data?.pageName;
  }

  drop(event: CdkDragDrop<ColumnType[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.updateOrder();
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.updateOrder();
    }
  }

  toggleSelectAll(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.selectAll = isChecked;
    this.columnsList.forEach((column) => (column.isHide = !isChecked));
  }

  initializeColumnsList() {
    this.columnsList = this.data?.columns.filter(
      (column: ColumnType) => column.name && !column.isSticky
    );
    this.columnsList.forEach((column) => {
      column.isHide = column.isHide || false;
    });

    this.columnsList = this.columnsList
      .map((item, index) => ({
        ...item,
        ['order']: item['order'] ?? index,
      }))
      .sort((a, b) => {
        const valueA = a['order'];
        const valueB = b['order'];
        return valueA - valueB;
      });

    // Update `selectAll` status based on individual checkboxes
    this.selectAll = this.columnsList.every((col) => !col.isHide);
  }

  updateOrder() {
    this.columnsList.forEach((column, index) => {
      column.order = index + 1;
    });
  }

  onClose() {
    this.dialogRef.close();
  }

  hideColumn(event: any, column: ColumnType) {
    column.isHide = !event.target.checked;

    // Update `selectAll` status based on individual checkboxes
    this.selectAll = this.columnsList.every((col) => !col.isHide);
  }

  onSubmit() {
    const visibleColumns = this.columnsList.filter((column) => !column.isHide);

    if (visibleColumns.length < 2) {
      const dialogConfig = {
        data: {
          title: 'common.error',
          message: 'messages.minimumVisibleColumnsMessage',
          status: DialogStatus.error,
        },
      };
      this.dialog.open(MessageDialogComponent, dialogConfig);
      return;
    }

    this.columnsList.forEach((column) => {
      TableColumnStateService.setData(
        this.pageName,
        column.name,
        'order',
        column.order
      );
      TableColumnStateService.setData(
        this.pageName,
        column.name,
        'isHide',
        column.isHide
      );
    });

    this.dialogRef.close(this.columnsList);
  }
}

interface ColumnType {
  title?: string;
  name?: string;
  order?: number;
  isHide?: boolean;
  isSticky?: boolean;
  pageName?: string;
}
